import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import BillingProfileService from '../services/BillingProfileService';
import {
  INDIA_GST_STATES,
  resolveGstStateSelection,
} from '../constants/indiaGstStates';

const EMPTY_FORM = {
  legal_name: '',
  gstin: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  pincode: '',
  email: '',
};

function MerchantBillingProfileDialog({
  open,
  defaultName = '',
  defaultEmail = '',
  onSaved,
  onCancel,
  allowDismiss = false,
}) {
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [gstStateCode, setGstStateCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const { profile } = await BillingProfileService.fetch();
        if (cancelled) return;
        if (profile) {
          setForm({
            legal_name: profile.legal_name || defaultName || '',
            gstin: profile.gstin || '',
            address_line1: profile.address_line1 || '',
            address_line2: profile.address_line2 || '',
            city: profile.city || '',
            state: profile.state || '',
            pincode: profile.pincode || '',
            email: profile.email || defaultEmail || '',
          });
          setGstStateCode(resolveGstStateSelection(null, profile.state));
        } else {
          setForm({
            ...EMPTY_FORM,
            legal_name: defaultName || '',
            email: defaultEmail || '',
          });
          setGstStateCode('');
        }
      } catch {
        if (!cancelled) {
          setForm({
            ...EMPTY_FORM,
            legal_name: defaultName || '',
            email: defaultEmail || '',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, defaultName, defaultEmail]);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    if (!gstStateCode) {
      setError('Please select a state');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await BillingProfileService.save(form);
      onSaved?.(result);
    } catch (e) {
      const message = e?.response?.data?.error?.message
        || e?.message
        || 'Failed to save billing profile';
      setError(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} maxWidth="sm" fullWidth onClose={allowDismiss ? onCancel : undefined}>
      <DialogTitle>Billing details for invoices</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          We need your billing address before you subscribe. GSTIN is optional if you are not GST registered.
        </Typography>
        {loading ? (
          <CircularProgress size={28} />
        ) : (
          <>
            <TextField
              fullWidth
              required
              margin="dense"
              label="Legal / business name"
              value={form.legal_name}
              onChange={(e) => updateField('legal_name', e.target.value)}
            />
            <TextField
              fullWidth
              margin="dense"
              label="GSTIN (optional)"
              value={form.gstin}
              onChange={(e) => updateField('gstin', e.target.value)}
            />
            <TextField
              fullWidth
              margin="dense"
              label="Billing email"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
            />
            <TextField
              fullWidth
              required
              margin="dense"
              label="Address line 1"
              value={form.address_line1}
              onChange={(e) => updateField('address_line1', e.target.value)}
            />
            <TextField
              fullWidth
              margin="dense"
              label="Address line 2"
              value={form.address_line2}
              onChange={(e) => updateField('address_line2', e.target.value)}
            />
            <TextField
              fullWidth
              required
              margin="dense"
              label="City"
              value={form.city}
              onChange={(e) => updateField('city', e.target.value)}
            />
            <TextField
              fullWidth
              required
              select
              margin="dense"
              label="State"
              value={gstStateCode}
              onChange={(e) => {
                const code = e.target.value;
                const row = INDIA_GST_STATES.find((s) => s.code === code);
                setGstStateCode(code);
                updateField('state', row?.name || '');
              }}
            >
              <MenuItem value="">Select state</MenuItem>
              {INDIA_GST_STATES.map((s) => (
                <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              required
              margin="dense"
              label="Pincode"
              value={form.pincode}
              onChange={(e) => updateField('pincode', e.target.value)}
            />
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {allowDismiss && (
          <Button onClick={onCancel} disabled={saving}>Later</Button>
        )}
        <Button variant="contained" onClick={handleSave} disabled={loading || saving}>
          {saving ? 'Saving…' : 'Save & continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MerchantBillingProfileDialog;
